/**
 * CrossFit WOD Generator Page
 *
 * 3-step wizard: Paste WOD -> Enter Profile -> Get Personalized WOD
 * Ported from strength-design-main/src/pages/CrossFitGenerator.tsx
 * All shadcn/Radix replaced with plain Tailwind. No framer-motion.
 */

import React, { useState, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Loader2,
  RefreshCw,
  Zap,
  Dumbbell,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Timer,
  Flame,
  Info,
  User,
  Settings2,
} from 'lucide-react';
import { NODE_BACKEND_URL } from '../../utils/constant';

// ─── Constants ──────────────────────────────────────────────────────────────────

const EXAMPLE_WODS = [
  { name: 'Fran', text: 'Fran: 21-15-9 Thrusters (95/65), Pull-ups' },
  { name: 'AMRAP', text: 'AMRAP 12: 10 Box Jumps (24), 15 KB Swings (53/35), 20 Air Squats' },
  { name: 'EMOM', text: 'EMOM 10: Odd - 5 Power Cleans (135/95), Even - 10 Burpees' },
];

const EXPERIENCE_LEVELS = [
  { value: 'foundations', label: 'Foundations', desc: 'New to CrossFit, learning movements' },
  { value: 'beginner', label: 'Beginner', desc: 'Under 6 months experience' },
  { value: 'intermediate', label: 'Intermediate', desc: '6 months to 2 years' },
  { value: 'rx', label: 'RX', desc: 'Can do most workouts as prescribed' },
  { value: 'rx+', label: 'RX+', desc: 'Competitor level, seeking extra challenge' },
];

const SCALING_PREFERENCES = [
  { value: 'conservative', label: 'Conservative', desc: 'Focus on form and safety' },
  { value: 'moderate', label: 'Moderate', desc: 'Balanced challenge' },
  { value: 'aggressive', label: 'Aggressive', desc: 'Push my limits' },
];

const COMMON_INJURIES = [
  { area: 'shoulder', label: 'Shoulder' },
  { area: 'knee', label: 'Knee' },
  { area: 'lower_back', label: 'Lower Back' },
  { area: 'wrist', label: 'Wrist' },
  { area: 'ankle', label: 'Ankle' },
  { area: 'elbow', label: 'Elbow' },
  { area: 'hip', label: 'Hip' },
];

const PR_FIELDS = [
  { key: 'backSquat', label: 'Back Squat' },
  { key: 'frontSquat', label: 'Front Squat' },
  { key: 'deadlift', label: 'Deadlift' },
  { key: 'clean', label: 'Clean' },
  { key: 'snatch', label: 'Snatch' },
  { key: 'strictPress', label: 'Strict Press' },
];

const TIER_COLORS = {
  'rx+': 'bg-purple-500 text-white',
  rx: 'bg-green-500 text-white',
  scaled: 'bg-blue-500 text-white',
  foundations: 'bg-orange-500 text-white',
};

const TIER_LABELS = {
  'rx+': 'RX+ (Competition)',
  rx: 'RX (As Prescribed)',
  scaled: 'Scaled',
  foundations: 'Foundations',
};

const cardClass = 'rounded-2xl border border-white/10 bg-[#0b1118] p-4';

// ─── Sub-Components ─────────────────────────────────────────────────────────────

function WODInputStep({ onParsed }) {
  const [wodText, setWodText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);

  const handleParse = async () => {
    if (!wodText.trim()) { setError('Please enter a WOD to parse'); return; }
    setParsing(true);
    setError(null);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/crossfit/parse-wod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wodText: wodText.trim() }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to parse WOD');
      }
      const { result } = await response.json();
      onParsed(result);
    } catch (err) {
      setError(err.message || 'Failed to parse WOD');
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        <span className="grid h-10 w-10 place-items-center rounded-xl border border-orange-400/45 bg-orange-400/10 text-orange-300">
          <Dumbbell size={18} />
        </span>
        <div>
          <h2 className="text-[15px] font-semibold text-zinc-100">Enter Your WOD</h2>
          <p className="text-[11px] text-zinc-500">Paste any CrossFit workout and we'll parse it automatically</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <span className="text-[11px] text-zinc-500 mr-1">Try an example:</span>
        {EXAMPLE_WODS.map((ex) => (
          <button
            key={ex.name}
            type="button"
            onClick={() => { setWodText(ex.text); setError(null); }}
            className="rounded-full border border-white/15 bg-white/[0.04] px-2.5 py-0.5 text-[10px] text-zinc-300 hover:bg-white/[0.08] transition-colors"
          >
            {ex.name}
          </button>
        ))}
      </div>

      <textarea
        placeholder="e.g., AMRAP 12: 10 Box Jumps (24), 15 KB Swings (53/35), 20 Air Squats"
        value={wodText}
        onChange={(e) => { setWodText(e.target.value); setError(null); }}
        disabled={parsing}
        className="w-full min-h-[120px] rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] font-mono text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400/60 focus:outline-none resize-none"
      />

      {error && (
        <div className="flex items-center gap-2 mt-2 text-rose-400 text-[11px]">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleParse}
        disabled={!wodText.trim() || parsing}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-orange-400/50 bg-gradient-to-r from-orange-500/85 to-amber-500/70 py-2.5 text-[12px] font-semibold text-white disabled:opacity-45 transition-opacity"
      >
        {parsing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {parsing ? 'Parsing WOD...' : 'Parse WOD'}
      </button>

      <div className="mt-3 text-[10px] text-zinc-500 space-y-0.5">
        <p><strong>Supported formats:</strong> AMRAP, For Time, EMOM, Tabata, Chipper, Strength</p>
        <p><strong>Load notation:</strong> (95/65) means 95lb for men, 65lb for women</p>
      </div>
    </div>
  );
}

function AthleteProfileStep({ parsedWOD, onProfileChange, initialProfile, onBack, onPersonalize, isPersonalizing, error }) {
  const [profile, setProfile] = useState({ ...initialProfile });
  const [selectedInjuries, setSelectedInjuries] = useState(
    (initialProfile.injuries || []).map((i) => i.area || i)
  );

  const update = (updates) => {
    const newProfile = { ...profile, ...updates };
    setProfile(newProfile);
    onProfileChange(newProfile);
  };

  const handleInjuryToggle = (area) => {
    const next = selectedInjuries.includes(area)
      ? selectedInjuries.filter((i) => i !== area)
      : [...selectedInjuries, area];
    setSelectedInjuries(next);
    update({ injuries: next.map((a) => ({ area: a, severity: 'moderate', affectsMovements: [] })) });
  };

  const handlePRChange = (key, value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) && value !== '') return;
    const newPRs = { ...profile.prs };
    if (value) {
      newPRs[key] = { value: numValue, unit: 'lb' };
    } else {
      delete newPRs[key];
    }
    update({ prs: newPRs });
  };

  return (
    <div className="space-y-3 transition-all duration-300">
      {parsedWOD && (
        <div className="rounded-xl border border-orange-400/35 bg-orange-400/10 px-3 py-2">
          <h3 className="text-[13px] font-semibold text-zinc-100">
            {parsedWOD.name || parsedWOD.type} WOD Parsed
          </h3>
          <p className="text-[10px] text-zinc-400">
            {parsedWOD.movements?.length || 0} movement(s) {parsedWOD.timeCapMinutes ? `- ${parsedWOD.timeCapMinutes} min cap` : ''}
          </p>
        </div>
      )}

      <div className={cardClass}>
        <div className="flex items-center gap-3 mb-4">
          <span className="grid h-10 w-10 place-items-center rounded-xl border border-orange-400/45 bg-orange-400/10 text-orange-300">
            <User size={18} />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-zinc-100">Athlete Profile</h2>
            <p className="text-[11px] text-zinc-500">Tell us about yourself for personalized scaling</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-[11px] text-zinc-400 mb-1 block">Name (optional)</label>
            <input
              type="text"
              placeholder="Your name"
              value={profile.name || ''}
              onChange={(e) => update({ name: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400/60 focus:outline-none"
            />
          </div>

          {/* Experience Level */}
          <div>
            <label className="text-[11px] text-zinc-400 mb-1 block">Experience Level</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {EXPERIENCE_LEVELS.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => update({ crossfitExperience: level.value })}
                  className={`rounded-xl border px-2 py-2 text-left transition-colors ${
                    profile.crossfitExperience === level.value
                      ? 'border-orange-400/70 bg-orange-400/10'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                  }`}
                >
                  <p className="text-[12px] text-zinc-100">{level.label}</p>
                  <p className="text-[10px] text-zinc-500">{level.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Scaling Preference */}
          <div>
            <label className="flex items-center gap-1 text-[11px] text-zinc-400 mb-1">
              <Settings2 size={12} /> Scaling Preference
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {SCALING_PREFERENCES.map((pref) => (
                <button
                  key={pref.value}
                  type="button"
                  onClick={() => update({ preferences: { ...profile.preferences, scalingStyle: pref.value } })}
                  className={`rounded-xl border px-2 py-2 text-center transition-colors ${
                    profile.preferences?.scalingStyle === pref.value
                      ? 'border-orange-400/70 bg-orange-400/10'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                  }`}
                >
                  <p className="text-[12px] text-zinc-100">{pref.label}</p>
                  <p className="text-[10px] text-zinc-500">{pref.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Injuries */}
          <div>
            <label className="flex items-center gap-1 text-[11px] text-zinc-400 mb-1">
              <AlertTriangle size={12} className="text-orange-400" /> Current Injuries/Limitations
            </label>
            <div className="flex flex-wrap gap-1.5">
              {COMMON_INJURIES.map((injury) => (
                <button
                  key={injury.area}
                  type="button"
                  onClick={() => handleInjuryToggle(injury.area)}
                  className={`rounded-full border px-2.5 py-1 text-[10px] transition-colors ${
                    selectedInjuries.includes(injury.area)
                      ? 'border-orange-400/70 bg-orange-500 text-white'
                      : 'border-white/15 bg-white/[0.02] text-zinc-400 hover:bg-white/[0.06]'
                  }`}
                >
                  {injury.label}
                </button>
              ))}
            </div>
            {selectedInjuries.length > 0 && (
              <p className="text-[10px] text-zinc-500 mt-1">We'll substitute movements that could aggravate these areas.</p>
            )}
          </div>

          {/* PRs */}
          <div>
            <label className="flex items-center gap-1 text-[11px] text-zinc-400 mb-1">
              <Dumbbell size={12} /> Personal Records (optional)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PR_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="text-[10px] text-zinc-500">{field.label}</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      placeholder="0"
                      value={profile.prs?.[field.key]?.value || ''}
                      onChange={(e) => handlePRChange(field.key, e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-[11px] text-zinc-100 focus:border-orange-400/60 focus:outline-none"
                    />
                    <span className="text-[10px] text-zinc-500">lb</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-[12px] text-zinc-200 hover:bg-white/[0.08] transition-colors"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          type="button"
          onClick={onPersonalize}
          disabled={isPersonalizing}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-orange-400/50 bg-gradient-to-r from-orange-500/85 to-amber-500/70 py-2 text-[12px] font-semibold text-white disabled:opacity-45 transition-opacity"
        >
          {isPersonalizing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {isPersonalizing ? 'Personalizing...' : 'Personalize My WOD'}
        </button>
      </div>
    </div>
  );
}

function MovementCard({ movement, index }) {
  const [expanded, setExpanded] = useState(false);
  const hasSubstitution = !!movement.substitutedFor;

  return (
    <div
      className={`rounded-xl border p-3 transition-colors ${
        hasSubstitution
          ? 'border-orange-400/40 bg-orange-400/5'
          : 'border-white/10 bg-white/[0.02]'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-zinc-100">{movement.name}</span>
            {hasSubstitution && (
              <span className="rounded-full border border-orange-400/50 px-1.5 py-0.5 text-[9px] text-orange-300">
                Substituted
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-zinc-400">
            <span className="font-medium">
              {typeof movement.reps === 'string' ? movement.reps : `${movement.reps} reps`}
            </span>
            {movement.loadAdjusted && (
              <span className="flex items-center gap-1">
                <Dumbbell size={10} /> {movement.loadAdjusted} lb
              </span>
            )}
            {movement.substitutedFor && (
              <span className="text-orange-300">(for {movement.substitutedFor})</span>
            )}
          </div>
        </div>
        {movement.reasoning && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-white/[0.06] rounded-lg transition-colors"
          >
            {expanded ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
          </button>
        )}
      </div>
      {expanded && movement.reasoning && (
        <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-zinc-400 flex items-start gap-2">
          <Info size={12} className="shrink-0 mt-0.5" />
          {movement.reasoning}
        </div>
      )}
    </div>
  );
}

function PersonalizedWODResult({ personalizedWOD, onBack, onReset }) {
  const [showWarmup, setShowWarmup] = useState(true);
  const { originalWOD, tier, movements, warmup, coachNotes, estimatedTimeMinutes, reasoning } =
    personalizedWOD;
  const hasSubstitutions = movements?.some((m) => m.substitutedFor);

  return (
    <div className="space-y-3 transition-all duration-300">
      {/* Header */}
      <div className={`${cardClass} border-orange-400/20`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold text-zinc-100">
                {originalWOD?.name || 'Your Personalized WOD'}
              </h2>
              {tier && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${TIER_COLORS[tier] || 'bg-zinc-600 text-white'}`}>
                  {TIER_LABELS[tier] || tier}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-400">
              {originalWOD?.type && (
                <span className="flex items-center gap-1"><Flame size={12} /> {originalWOD.type}</span>
              )}
              {(estimatedTimeMinutes || originalWOD?.timeCapMinutes) && (
                <span className="flex items-center gap-1">
                  <Timer size={12} /> {estimatedTimeMinutes || originalWOD.timeCapMinutes} min
                </span>
              )}
            </div>
          </div>
          {hasSubstitutions && (
            <div className="flex items-center gap-1 text-orange-400">
              <AlertTriangle size={16} />
              <span className="text-[11px] font-medium">Modified</span>
            </div>
          )}
        </div>
      </div>

      {/* Warmup */}
      {warmup && warmup.length > 0 && (
        <div className={cardClass}>
          <button
            type="button"
            onClick={() => setShowWarmup(!showWarmup)}
            className="flex items-center justify-between w-full"
          >
            <span className="flex items-center gap-2 text-[13px] font-medium text-zinc-100">
              <CheckCircle2 size={14} className="text-green-400" /> Warmup
            </span>
            {showWarmup ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
          </button>
          {showWarmup && (
            <ul className="mt-2 space-y-1">
              {warmup.map((item, idx) => (
                <li key={idx} className="flex items-center gap-2 text-[11px] text-zinc-400">
                  <span className="w-5 h-5 rounded-full bg-orange-400/10 text-orange-300 text-[10px] flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Movements */}
      <div className={cardClass}>
        <h3 className="flex items-center gap-2 text-[13px] font-medium text-zinc-100 mb-3">
          <Dumbbell size={14} className="text-orange-300" /> Workout
        </h3>
        <div className="space-y-2">
          {(movements || []).map((movement, idx) => (
            <MovementCard key={idx} movement={movement} index={idx} />
          ))}
        </div>
      </div>

      {/* Coach Notes */}
      {coachNotes && (
        <div className="rounded-2xl border border-blue-400/20 bg-blue-400/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-blue-400 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-[12px] font-medium text-blue-300 mb-1">Coach Notes</h4>
              <p className="text-[11px] text-blue-200/80">{coachNotes}</p>
            </div>
          </div>
        </div>
      )}

      {reasoning && (
        <p className="text-[10px] text-zinc-500 text-center">{reasoning}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-[12px] text-zinc-200 hover:bg-white/[0.08] transition-colors"
        >
          <ArrowLeft size={14} /> Adjust Profile
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-[12px] text-zinc-200 hover:bg-white/[0.08] transition-colors"
        >
          <RefreshCw size={14} /> New WOD
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

const STEPS = ['input', 'profile', 'result'];
const STEP_LABELS = { input: 'Enter WOD', profile: 'Your Profile', result: 'Personalized' };

export default function CrossFitGeneratorPage() {
  const [step, setStep] = useState('input');
  const [parsedWOD, setParsedWOD] = useState(null);
  const [athleteProfile, setAthleteProfile] = useState({
    id: 'guest',
    userId: 'guest',
    name: '',
    crossfitExperience: 'intermediate',
    injuries: [],
    prs: {},
    preferences: { scalingStyle: 'moderate' },
  });
  const [personalizedWOD, setPersonalizedWOD] = useState(null);
  const [isPersonalizing, setIsPersonalizing] = useState(false);
  const [error, setError] = useState(null);

  const handleWODParsed = useCallback((wod) => {
    setParsedWOD(wod);
    setStep('profile');
    setError(null);
  }, []);

  const handlePersonalize = useCallback(async () => {
    if (!parsedWOD) return;
    setIsPersonalizing(true);
    setError(null);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/crossfit/personalize-wod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wod: parsedWOD,
          athleteProfile: {
            ...athleteProfile,
            id: athleteProfile.id || 'guest',
            userId: athleteProfile.userId || 'guest',
            name: athleteProfile.name || 'Athlete',
          },
          boxContext: {
            id: 'default',
            equipment: {
              pullUpRigs: 12,
              barbells: 20,
              rowers: 10,
              boxes: [{ height: 20, count: 10 }, { height: 24, count: 10 }],
              kettlebells: [{ weight: 35, count: 10 }, { weight: 53, count: 10 }],
            },
          },
        }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to personalize WOD');
      }
      const { result } = await response.json();
      setPersonalizedWOD(result);
      setStep('result');
    } catch (err) {
      setError(err.message || 'Failed to personalize WOD');
    } finally {
      setIsPersonalizing(false);
    }
  }, [parsedWOD, athleteProfile]);

  const handleReset = useCallback(() => {
    setStep('input');
    setParsedWOD(null);
    setPersonalizedWOD(null);
    setError(null);
  }, []);

  const handleBack = useCallback(() => {
    if (step === 'profile') setStep('input');
    else if (step === 'result') setStep('profile');
  }, [step]);

  const currentStepIndex = STEPS.indexOf(step);

  return (
    <div className="min-h-full bg-[#040608] text-zinc-100 p-4 md:p-6">
      <div className="mx-auto max-w-3xl space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-400/45 bg-orange-400/10 text-orange-300 text-[11px] font-medium mb-3">
            <Zap size={12} /> AI-Powered CrossFit
          </div>
          <h1 className="text-2xl font-bold text-white">WOD Personalizer</h1>
          <p className="text-[12px] text-zinc-500 mt-1">
            Enter any CrossFit workout and get it scaled perfectly for you
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-3 mb-4">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium transition-colors ${
                    step === s
                      ? 'bg-orange-500 text-white'
                      : idx < currentStepIndex
                      ? 'bg-orange-400/20 text-orange-300'
                      : 'bg-white/10 text-zinc-500'
                  }`}
                >
                  {idx + 1}
                </div>
                <span className={`text-[11px] ${step === s ? 'text-zinc-100 font-medium' : 'text-zinc-500'}`}>
                  {STEP_LABELS[s]}
                </span>
              </div>
              {idx < 2 && <ArrowRight size={12} className="text-zinc-600 mx-1" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        {step === 'input' && <WODInputStep onParsed={handleWODParsed} />}

        {step === 'profile' && (
          <AthleteProfileStep
            parsedWOD={parsedWOD}
            onProfileChange={setAthleteProfile}
            initialProfile={athleteProfile}
            onBack={handleBack}
            onPersonalize={handlePersonalize}
            isPersonalizing={isPersonalizing}
            error={error}
          />
        )}

        {step === 'result' && personalizedWOD && (
          <PersonalizedWODResult
            personalizedWOD={personalizedWOD}
            onBack={handleBack}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
}
